// src/hooks/useToolResults.js
import {useEffect, useState, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {API_BASE} from '../../apiBase';
import {apiFetch} from '../../api/api';
import i18n from '../../i18n';

export default function useToolResults(files, toolId) {
    const [results, setResults] = useState([]);
    const resultsRef = useRef([]);
    const [toolDetails, setToolDetails] = useState(null);
    const [editedValues, setEditedValues] = useState({});
    const [editingCell, setEditingCell] = useState(null);
    const [editValue, setEditValue] = useState("");
    const hasProcessed = useRef(false);
    const navigate = useNavigate();

    // --- Helpers for stable resume key & persistence -------------------------
    const makeBatchKey = () => {
        if (!files || !Array.isArray(files)) return null;
        const sig = files
            .map(({file}) => `${file?.name || ''}:${file?.size || 0}:${file?.lastModified || 0}`)
            .join('|');
        return `tool-results:${toolId}:${sig}`;
    };
    const batchKeyRef = useRef(null);
    if (!batchKeyRef.current) batchKeyRef.current = makeBatchKey();
    const BATCH_KEY = batchKeyRef.current;

    // Stable per-file key (used for idempotency against backend and React key)
    const makeItemKey = (file) =>
        `${toolId}::${file?.name || ''}::${file?.size || 0}::${file?.lastModified || 0}`;

    const loadSaved = () => {
        try {
            if (!BATCH_KEY) return null;
            const raw = localStorage.getItem(BATCH_KEY);
            if (!raw) return null;
            return JSON.parse(raw);
        } catch {
            return null;
        }
    };
    const saveProgress = (next) => {
        try {
            if (!BATCH_KEY) return;
            localStorage.setItem(BATCH_KEY, JSON.stringify({
                results: next,
                editedValues,
            }));
        } catch {
        }
    };

    // keep editedValues in storage, too, so reload preserves inline edits
    useEffect(() => {
        if (!BATCH_KEY) return;
        try {
            const raw = localStorage.getItem(BATCH_KEY);
            const parsed = raw ? JSON.parse(raw) : {};
            localStorage.setItem(BATCH_KEY, JSON.stringify({...parsed, editedValues}));
        } catch {
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(editedValues), BATCH_KEY]);

    useEffect(() => {
        if (!files || !toolId || hasProcessed.current) return;
        hasProcessed.current = true;

        const token = localStorage.getItem("token");
        if (!token) {
            alert("No token found. Please log in again.");
            navigate('/');
            return;
        }

        // Seed from saved progress if present; otherwise initialize fresh
        const saved = loadSaved();
        // Start with saved or a fresh baseline, and ensure each row has an itemKey
        const baseline = (saved?.results && Array.isArray(saved.results))
            ? saved.results.map((r, idx) => ({
                ...r,
                originalIndex: typeof r.originalIndex === 'number' ? r.originalIndex : idx,
                itemKey: r.itemKey || makeItemKey(files[idx]?.file),
            }))
            : files.map(({file}, idx) => ({
                fileName: file.name,
                originalIndex: idx,
                status: 'pending',     // not yet sent
                data: null,
                itemKey: makeItemKey(file),
            }));

        // Ensure all annotations have stable IDs
        baseline.forEach(res => {
            const annotations = res?.data?.annotations || [];
            annotations.forEach((a, aIdx) => {
                if (!a.id) {
                    a.id = `ann_${res.data?.execution_id}_${aIdx}`;
                }
            });
        });

        setResults(baseline);
        resultsRef.current = baseline;
        if (!saved?.results) saveProgress(baseline);

        const processFiles = async () => {
            // Build a stable set of completed indices from the baseline snapshot
            const completed = new Set(
                baseline.map((r, i) => (r?.status === 'success' ? i : null)).filter(i => i !== null)
            );

            for (const [idx, {file}] of files.entries()) {
                // If this file already finished earlier, skip re-sending
                if (completed.has(idx)) continue; // ✅ prevents reprocessing on reload
                // (Optionally: if you want to skip previous errors too, add || r.status==='error')

                // Mark as loading & persist
                setResults(prev => {
                    const next = prev.map((r, i) => i === idx ? {...r, status: 'loading'} : r);
                    resultsRef.current = next;
                    saveProgress(next);
                    return next;
                });
                const formData = new FormData();
                formData.append("pdf", file);
                formData.append("tool_id", toolId);
                formData.append("language", (i18n && i18n.language) || 'en');
                // 👇 Idempotency: same key across reloads prevents duplicate history entries server-side
                const row = baseline[idx];
                formData.append("idempotency_key", row.itemKey);

                try {
                    const response = await apiFetch(`${API_BASE}/run_tool`, {
                        method: 'POST',
                        body: formData, // no Content-Type header — browser sets it for FormData
                    });

                    let result = {};
                    try {
                        result = await response.json();
                    } catch {
                        // non-JSON response, leave result as {}
                    }

                    if (!response.ok) {
                        setResults((prev) => {
                            const next = prev.map((r, i) =>
                                i === idx ? {...r, status: 'error', error: result.error} : r
                            );
                            resultsRef.current = next;
                            saveProgress(next);
                            return next;
                        });
                        continue;
                    }

                    setResults(prev => {
                        const next = prev.map((r, i) =>
                            i === idx
                                ? {
                                    ...r,
                                    status: 'success',
                                    data: result,
                                    // keep display filename but DO NOT use it for identity
                                    fileName: result.file_name || file.name,
                                    itemKey: prev[i]?.itemKey || row.itemKey,
                                }
                                : r
                        );
                        // Ensure all annotations have stable IDs
                        next.forEach(res => {
                            const annotations = res?.data?.annotations || [];
                            annotations.forEach((a, aIdx) => {
                                if (!a.id) {
                                    a.id = `ann_${res.data?.execution_id}_${aIdx}`;
                                }
                            });
                        });
                        // Track as completed within this run too
                        completed.add(idx);
                        resultsRef.current = next;
                        saveProgress(next);
                        return next;
                    });
                } catch (error) {
                    setResults(prev => {
                        const next = prev.map((r, i) =>
                            i === idx ? {...r, status: 'error', error: error.message} : r
                        );
                        resultsRef.current = next;
                        saveProgress(next);
                        return next;
                    });
                }
            }
        };
        processFiles();
    }, [files, toolId, navigate]);

    useEffect(() => {
        if (!files || !toolId) return;

        const token = localStorage.getItem("token");
        if (!token) {
            alert("No token found. Please log in again.");
            navigate('/');
            return;
        }

        const fetchToolDetails = async () => {
            try {
                const res = await apiFetch(`${API_BASE}/get_tool_details/${toolId}`, {
                    method: 'GET',
                });

                if (res.ok) {
                    const data = await res.json();
                    setToolDetails(data);
                } else {
                    console.error('Failed to fetch tool details');
                }
            } catch (error) {
                console.error('Error fetching tool details:', error);
            }
        };

        fetchToolDetails();
    }, [files, toolId, navigate]);

    // Keep a ref mirror and save the latest progress on unload
    useEffect(() => {
        resultsRef.current = results;
    }, [results]);
    // On hard reload or tab close, make sure latest progress is saved
    useEffect(() => {
        const handler = () => saveProgress(results);
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(results), BATCH_KEY]);

    return {
        results,
        setResults,
        toolDetails,
        editedValues,
        setEditedValues,
        editingCell,
        setEditingCell,
        editValue,
        setEditValue,
    };
}
